import { useEffect, useRef, useState } from 'react'
import type { ComplianceDocumentSectionRecord } from '@artem-lex/compliance-documents'
import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import {
  isSupportedLanguage,
  type SupportedLanguage,
} from '@/shared/config/languages'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { ComplianceDocumentGenerateMenu } from '@/features/compliance-studio/machinery-directive/components/ComplianceDocumentGenerateMenu'
import { ComplianceDocumentManifestProvider } from '@/features/compliance-studio/shared/compliance-document/ComplianceDocumentManifestContext'
import {
  COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS,
  ComplianceDocumentStreamingPanel,
} from '@/features/compliance-studio/shared/compliance-document/ComplianceDocumentStreamingPanel'
import { ComplianceDocumentView } from '@/features/compliance-studio/shared/compliance-document/ComplianceDocumentView'
import {
  generateCraRiskAssessment,
  useExportCraRiskAssessmentPdf,
  useGetCraStudio,
  useSaveCraRiskAssessment,
} from './api'

const CRA_LOADING_INTRO = `1. Introduction

This Cybersecurity Risk Assessment of a product with digital elements has been prepared to assess the cybersecurity risks against which the product is designed, developed, produced, delivered and maintained pursuant to Article 13 of Regulation (EU) 2024/2847 (Cyber Resilience Act).

This assessment is prepared with reference to ISO/IEC 27005:2022.

2. Risk assessment procedure

The assessment analyses the cybersecurity risks based on the intended purpose and reasonably foreseeable use of the product, and how the essential cybersecurity requirements in Annex I are implemented.`

const CRA_LOADING_RISKS = `

3.1.3 Risk assessment

**Unchanged default credentials**

| | |
| --- | --- |
| **Risk identification** | |
| Risk | Unchanged default credentials |
| Risk description (asset-based approach) | The product ships with a factory administrator password printed in the installation guide. Someone on the same network can sign in and change the configuration. Vulnerability: lack of authentication. Threat: unauthorised access. |
| Risk owner | Product security |
| **Risk analysis** | |
| Potential consequences | Configuration and stored data can be changed. 3 - Serious |
| Likelihood of occurrence | The password is published with the product. 4 - Very likely |
| Level of risk | High |
| **Risk evaluation** | |
| Risk evaluation | Unacceptable. A reduction measure is required in the short term. |
| **Risk treatment** | |
| Risk treatment option | Mitigation |
| Controls | Force a unique password on first start, and disable the factory account. |

**Missing security updates**

| | |
| --- | --- |
| **Risk identification** | |
| Risk | Missing security updates |
| Risk description (asset-based approach) | The firmware has no supported path for a security update. A known flaw in the network stack stays in every unit in the field. Vulnerability: well-known flaws in the software. Threat: exploitation of a published vulnerability. |
| Risk owner | Product security |
| **Risk analysis** | |
| Potential consequences | Remote interruption of the product's basic functions. 4 - Critical |
| Likelihood of occurrence | The flaw is public and the product cannot be patched. 3 - Likely |
| Level of risk | High |
| **Risk evaluation** | |
| Risk evaluation | Unacceptable until an update mechanism is in place. |
| **Risk treatment** | |
| Risk treatment option | Mitigation |
| Controls | Provide signed security updates and apply them by default. |
`

const CRA_LOADING_PREVIEW = CRA_LOADING_INTRO + CRA_LOADING_RISKS

const CRA_LOADING_PREVIEW_DE = `1. Einleitung

Diese Cybersicherheitsrisikobewertung wurde erstellt, um die Cybersicherheitsrisiken eines Produkts mit digitalen Elementen gemäß Artikel 13 der Verordnung (EU) 2024/2847 (Cyberresilienz-Verordnung) zu bewerten.

2. Verfahren zur Cybersicherheitsrisikobewertung

Die Bewertung analysiert die Cybersicherheitsrisiken auf Grundlage der bestimmungsgemäßen und der vernünftigerweise vorhersehbaren Verwendung.

3.4 Risikobewertung

**Unveränderte Standardzugangsdaten**

| | |
| --- | --- |
| **Risikoidentifikation** | |
| Risiko | Unveränderte Standardzugangsdaten |
| Risikobeschreibung (Asset-basierter Ansatz) | Das Produkt wird mit einem werkseitigen Administratorkennwort ausgeliefert, das in der Installationsanleitung steht. Eine Person im selben Netzwerk kann sich anmelden und die Konfiguration ändern. |
| Risikoverantwortliche Person | Produktsicherheit |
| **Risikoanalyse** | |
| Potenzielle Folgen | Konfiguration und gespeicherte Daten können verändert werden. 3 - Schwerwiegend |
| Eintrittswahrscheinlichkeit | Das Kennwort wird mit dem Produkt veröffentlicht. 4 - Sehr wahrscheinlich |
| Risikostufe | Hoch |
| **Risikobewertung** | |
| Risikobewertung | Nicht akzeptabel. Kurzfristig ist eine Maßnahme zur Risikominderung erforderlich. |
| **Risikobehandlung** | |
| Optionen zur Risikobehandlung | Risikominderung |
| Risikokontrollen | Beim ersten Start ein eigenes Kennwort erzwingen und das Werkskonto deaktivieren. |

**Fehlende Sicherheitsaktualisierungen**

| | |
| --- | --- |
| **Risikoidentifikation** | |
| Risiko | Fehlende Sicherheitsaktualisierungen |
| Risikobeschreibung (Asset-basierter Ansatz) | Für die Firmware gibt es keinen unterstützten Weg für eine Sicherheitsaktualisierung. Eine bekannte Schwachstelle im Netzwerkstack bleibt in jedem ausgelieferten Gerät bestehen. |
| Risikoverantwortliche Person | Produktsicherheit |
| **Risikoanalyse** | |
| Potenzielle Folgen | Fernunterbrechung der grundlegenden Funktionen des Produkts. 4 - Kritisch |
| Eintrittswahrscheinlichkeit | Die Schwachstelle ist öffentlich und das Produkt kann nicht gepatcht werden. 3 - Wahrscheinlich |
| Risikostufe | Hoch |
| **Risikobewertung** | |
| Risikobewertung | Nicht akzeptabel, solange kein Aktualisierungsmechanismus vorhanden ist. |
| **Risikobehandlung** | |
| Optionen zur Risikobehandlung | Risikominderung |
| Risikokontrollen | Signierte Sicherheitsaktualisierungen bereitstellen und standardmäßig anwenden. |
`

function loadingPreview(language: 'en-GB' | 'de-DE') {
  if (language === 'de-DE') {
    const introEnd = CRA_LOADING_PREVIEW_DE.indexOf('3.4 Risikobewertung')
    return {
      text: CRA_LOADING_PREVIEW_DE,
      riskAt: introEnd,
    }
  }
  return {
    text: CRA_LOADING_PREVIEW,
    riskAt: CRA_LOADING_INTRO.length,
  }
}

function startLoadingPreview(
  language: 'en-GB' | 'de-DE',
  onText: (text: string) => void,
  onGeneratingRisks: () => void,
  shouldStop: () => boolean
) {
  const preview = loadingPreview(language)
  let index = 0
  let announcedRisks = false
  const timer = window.setInterval(() => {
    if (shouldStop() || index >= preview.text.length) {
      window.clearInterval(timer)
      return
    }
    index = Math.min(index + 24, preview.text.length)
    if (!announcedRisks && index > preview.riskAt) {
      announcedRisks = true
      onGeneratingRisks()
    }
    onText(preview.text.slice(0, index))
  }, 40)
  return timer
}

export function CraRiskAssessmentPage() {
  const { t } = useTranslation([
    'craCompliance',
    'machineryDirectiveCompliance',
  ])
  const { craStudioId = '' } = useParams()
  const studioQuery = useGetCraStudio(craStudioId)
  const save = useSaveCraRiskAssessment(craStudioId)
  const exportPdf = useExportCraRiskAssessmentPdf(craStudioId)
  const [sections, setSections] = useState<
    ComplianceDocumentSectionRecord[] | null
  >(null)
  const [contentSyncKey, setContentSyncKey] = useState(0)
  const [isGenerating, setIsGenerating] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [streamingSectionTitle, setStreamingSectionTitle] = useState<
    string | null
  >(null)
  const [previewGeneratingRisks, setPreviewGeneratingRisks] = useState(false)
  const [generationLanguage, setGenerationLanguage] =
    useState<SupportedLanguage>('en-GB')
  const [error, setError] = useState<string | null>(null)
  const [generationNote, setGenerationNote] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const loadedKey = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const previewTimer = useRef<number | null>(null)
  const realStreamStarted = useRef(false)
  const languageSynced = useRef(false)

  useEffect(() => {
    const studio = studioQuery.data
    if (!studio) {
      return
    }
    const key = `${studio.id}:${studio.generatedAt ?? 'none'}`
    if (loadedKey.current === key) {
      return
    }
    loadedKey.current = key
    setSections(
      studio.sections == null
        ? null
        : studio.sections.filter((section) => section.sectionId !== 'cover')
    )
    setContentSyncKey((value) => value + 1)
  }, [studioQuery.data])

  useEffect(() => {
    const contentLanguage = studioQuery.data?.contentLanguage
    if (languageSynced.current || contentLanguage == null) {
      return
    }
    if (isSupportedLanguage(contentLanguage)) {
      setGenerationLanguage(contentLanguage)
      languageSynced.current = true
    }
  }, [studioQuery.data?.contentLanguage])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      if (previewTimer.current != null) {
        window.clearInterval(previewTimer.current)
      }
    }
  }, [])

  const hasDocument = sections != null && sections.length > 0
  const hasIngestedSources = studioQuery.data?.hasIngestedSources === true

  const runGenerate = async (language: 'en-GB' | 'de-DE') => {
    setConfirmOpen(false)
    setError(null)
    setGenerationNote(null)
    setIsGenerating(true)
    setStreamingContent('')
    setStreamingSectionTitle(null)
    setPreviewGeneratingRisks(false)
    realStreamStarted.current = false
    if (previewTimer.current != null) {
      window.clearInterval(previewTimer.current)
    }
    previewTimer.current = startLoadingPreview(
      language,
      (text) => {
        if (!realStreamStarted.current) {
          setStreamingContent(text)
        }
      },
      () => {
        if (!realStreamStarted.current) {
          setPreviewGeneratingRisks(true)
        }
      },
      () => realStreamStarted.current
    )
    const controller = new AbortController()
    abortRef.current = controller
    try {
      await generateCraRiskAssessment({
        studioId: craStudioId,
        language,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === 'status') {
            setGenerationNote(`Workflow run ${event.workflowRunId}.`)
            return
          }
          if (event.type === 'section.started') {
            realStreamStarted.current = true
            if (previewTimer.current != null) {
              window.clearInterval(previewTimer.current)
              previewTimer.current = null
            }
            setPreviewGeneratingRisks(false)
            setStreamingSectionTitle(event.title)
            setStreamingContent('')
            setGenerationNote(`Workflow run ${event.workflowRunId}.`)
            return
          }
          if (event.type === 'section.progress') {
            setStreamingContent((previous) => previous + event.text)
            return
          }
          if (event.type === 'error') {
            setError(
              event.workflowRunId
                ? `${event.error} Workflow run ${event.workflowRunId}.`
                : event.error
            )
            return
          }
          loadedKey.current = `${craStudioId}:${event.generatedAt}`
          setSections(event.sections)
          setContentSyncKey((value) => value + 1)
          setGenerationNote(
            event.emptyReason
              ? `${event.emptyReason} Workflow run ${event.workflowRunId}.`
              : null
          )
        },
      })
    } catch (caught) {
      if (!controller.signal.aborted) {
        setError(caught instanceof Error ? caught.message : String(caught))
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
      }
      realStreamStarted.current = true
      if (previewTimer.current != null) {
        window.clearInterval(previewTimer.current)
        previewTimer.current = null
      }
      setIsGenerating(false)
      window.setTimeout(() => {
        setStreamingContent('')
        setStreamingSectionTitle(null)
        setPreviewGeneratingRisks(false)
      }, COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS)
    }
  }

  const requestGenerate = (language: SupportedLanguage) => {
    if (language === 'zh-CN') {
      return
    }
    if (hasDocument) {
      setConfirmOpen(true)
      return
    }
    void runGenerate(language)
  }

  const statusLabel =
    streamingSectionTitle != null
      ? t('riskAssessmentPage.generatingSection', {
          ns: 'machineryDirectiveCompliance',
          section: streamingSectionTitle,
        })
      : generationLanguage === 'de-DE'
        ? previewGeneratingRisks
          ? 'CRA-Risiken werden erstellt'
          : 'Produktdokumente werden gelesen'
        : previewGeneratingRisks
          ? t('riskAssessment.generatingRisks')
          : t('riskAssessment.reading')

  if (studioQuery.isLoading) {
    return <Skeleton className='h-64 w-full' />
  }

  let generateButtonClassName: string | undefined
  if (!hasDocument) {
    generateButtonClassName = 'gap-2'
  }

  const generateButton = (
    <ComplianceDocumentGenerateMenu
      isRegenerate={hasDocument}
      fullDocumentOnly={!hasDocument}
      size='sm'
      className={generateButtonClassName}
      withFileIcon={!hasDocument}
      translationNamespace='machineryDirectiveCompliance'
      translationKeyPrefix='riskAssessmentPage'
      generationLanguage={generationLanguage}
      onGenerationLanguageChange={setGenerationLanguage}
      onGenerate={(payload) => {
        requestGenerate(payload.language ?? generationLanguage)
      }}
      isGeneratePending={isGenerating}
      isGenerateActionDisabled={
        !hasIngestedSources || generationLanguage === 'zh-CN'
      }
    />
  )

  const streamingPanel = (
    <ComplianceDocumentStreamingPanel
      isOpen={isGenerating}
      statusLabel={statusLabel}
      streamingContent={streamingContent}
    />
  )

  return (
    <ComplianceDocumentManifestProvider
      documentKind='risk_assessment'
      locale={generationLanguage === 'de-DE' ? 'de-DE' : 'en-GB'}
    >
      <div
        className={
          hasDocument
            ? 'flex h-[calc(100dvh-11rem)] min-h-80 flex-col gap-4 overflow-hidden'
            : 'flex h-full flex-col space-y-4'
        }
      >
        <h1 className='text-2xl font-semibold tracking-tight'>
          {t('riskAssessment.pageTitle')}
        </h1>
        {!hasIngestedSources && !studioQuery.isLoading ? (
          <p className='text-sm text-muted-foreground'>
            {t('riskAssessment.generateDisabled')}
          </p>
        ) : null}
        {error ? (
          <p className='text-sm text-destructive' role='alert'>
            {error}
          </p>
        ) : null}
        {generationNote ? (
          <p className='text-sm text-muted-foreground'>{generationNote}</p>
        ) : null}
        {isGenerating && !hasDocument ? streamingPanel : null}
        {!isGenerating && !hasDocument ? (
          <Empty className='min-h-[320px] rounded-lg border border-dashed'>
            <EmptyHeader>
              <EmptyMedia variant='icon'>
                <FileText className='size-10' />
              </EmptyMedia>
              <EmptyTitle>{t('riskAssessment.pageTitle')}</EmptyTitle>
              <EmptyDescription>
                {t('riskAssessment.emptyDescription')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>{generateButton}</EmptyContent>
          </Empty>
        ) : null}
        {hasDocument ? (
          <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
            <ComplianceDocumentView
              studioScopeId={craStudioId}
              sections={sections}
              onSectionsChange={setSections}
              onAutosave={async (next) => {
                await save.mutateAsync(next)
                setLastSavedAt(new Date())
              }}
              onCreateVersion={async () => {}}
              isAutosaving={save.isPending}
              isCreatingVersion={false}
              lastSavedAt={lastSavedAt}
              isGenerating={isGenerating}
              onExportPdf={async () => {
                try {
                  await exportPdf.mutateAsync()
                } catch {
                  toast.error(
                    t('riskAssessmentPage.exportFailed', {
                      ns: 'machineryDirectiveCompliance',
                    })
                  )
                }
              }}
              isExportPending={exportPdf.isPending}
              generateMenu={generateButton}
              onRegenerateSection={() => {}}
              contentSyncKey={contentSyncKey}
              sectionDomIdPrefix='cra-ra'
              translationKeyPrefix='riskAssessmentPage'
              i18nNamespace='machineryDirectiveCompliance'
              inputIdPrefix='cra-ra'
              streamingPanel={isGenerating ? streamingPanel : undefined}
              bubbleMenuContent={
                <Button type='button' size='sm' variant='ghost'>
                  {t('riskAssessmentPage.rewrite', {
                    ns: 'machineryDirectiveCompliance',
                  })}
                </Button>
              }
            />
          </div>
        ) : null}
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                'riskAssessmentPage.fullRegenerateCustomSectionsDialog.title',
                { ns: 'machineryDirectiveCompliance' }
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'riskAssessmentPage.fullRegenerateCustomSectionsDialog.description',
                { ns: 'machineryDirectiveCompliance' }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('riskAssessment.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (generationLanguage === 'zh-CN') {
                  return
                }
                void runGenerate(generationLanguage)
              }}
            >
              {t(
                'riskAssessmentPage.fullRegenerateCustomSectionsDialog.confirm',
                { ns: 'machineryDirectiveCompliance' }
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ComplianceDocumentManifestProvider>
  )
}
